import { Suspense, lazy } from "react";

import moment from "moment";
import { CookiesProvider } from "react-cookie";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import { useEffectOnce } from "usehooks-ts";

import { ApolloProvider } from "@apollo/client";
import { DevelopmentAnalyticsDataErrorBoundary } from "@components/development-analytics-data-error-boundary";
import { LogOnMount } from "@components/v2/analytics";
import { datadogRum } from "@datadog/browser-rum";
import { CommandPalette } from "@features/command-palette";
import {
  UniversalBenchmarksModalProvider,
  UniversalBenchmarksProvider,
} from "@features/universal-benchmarks";
import { FeatureFlagsProvider } from "@hooks/http";
import { UserDetailsProvider } from "@hooks/http/use-user-enrichment";
import { useV2ColorDesigns } from "@hooks/use-v2-color-designs";
import { UserAttributionSettingProvider } from "@hooks/user-attribution-settings-context";
import { bugsnagClient } from "@internal/analytics";
import { AttributionLayout } from "@pages/attribution";
import { ProductAnalytics } from "@pages/product-analytics";
import { NotificationManager } from "@shared/notify";

import { Chrome } from "./components/chrome";
import { AppVersionProvider } from "./components/data/app-version";
import { CustomGoalsProvider } from "./components/data/custom-goals-provider";
import { CustomerBreakdownConfigProvider } from "./components/data/customer-breakdown-configs";
import { SalesBreakdownConfigsProvider } from "./components/data/sales-breakdown-configs";
import { Page404 } from "./components/title-slide-view";
import { useUser } from "./components/user-context";
import config, { envName } from "./environment";
import { OrdersV2 } from "./pages/orders-v2/orders-v2";
import { PreActivationContent } from "./pre-activation";
import { type Appcues } from "./types/appcues";
import { Redirects } from "./utils/redirects";

import { MaintenanceBoundary } from ".";

const CreativeAnalyticsPage = lazy(() => import("@pages/creative-analytics"));
const CustomersRoutes = lazy(() => import("@pages/customers"));
const FarmstandPurchasesTablePage = lazy(
  () => import("@pages/farmstand-purchases"),
);
const FastSalesPage = lazy(() => import("@pages/sales-v2"));
const GuideRoutes = lazy(() => import("@pages/guides"));
const MMMPage = lazy(() => import("@pages/mmm"));
const ModelComparisonPage = lazy(() => import("@pages/model-comparison"));
const MonitorRoutes = lazy(() => import("@pages/monitor"));
const ObjectEditorRoutes = lazy(() => import("@pages/objects"));
const OrderPage = lazy(() => import("@pages/order"));
const OrdersPage = lazy(() => import("@pages/orders"));
const OverviewPage = lazy(() => import("@pages/overview"));
const OverviewPrintPage = lazy(() => import("@pages/overview/print-view"));
const OverviewV2Page = lazy(() => import("@pages/overview-v2"));
const ProgressPage = lazy(() => import("@pages/demo/progress-report"));
const SalesV3Page = lazy(() => import("@pages/sales-v3"));
const SalesV4Page = lazy(() => import("@pages/sales-v4"));
const SettingsRoutes = lazy(() => import("@pages/settings"));
const BreakdownsRoutes = lazy(() => import("@pages/breakdowns"));
const LabelAssignmentPage = lazy(
  () => import("@pages/breakdowns/label-assignment-page"),
);
const MetricsExplorerPage = lazy(() => import("@pages/metrics-explorer"));
const LabsPage = lazy(() => import("@pages/labs"));
const LandingPageAnalyticsPage = lazy(
  () => import("@pages/landing-page-analytics"),
);
const NotificationsPage = lazy(() => import("@pages/notifications"));

declare global {
  interface Crypto {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    randomUUID: () => string;
  }
  interface Window {
    Appcues: Appcues;
  }
}

if (config.datadogApplicationId && config.datadogClientToken) {
  datadogRum.init({
    applicationId: config.datadogApplicationId,
    clientToken: config.datadogClientToken,
    site: "us5.datadoghq.com",
    service: "nb-frontend",
    env: envName,
    sessionSampleRate: 100,
    premiumSampleRate: 100,
    trackUserInteractions: true,
    defaultPrivacyLevel: "mask-user-input",
    version: "1.0.0",
    // Remove log rocket from activity URLs
    excludedActivityUrls: [/.*r\.lr-in-prod\.com.*/],
  });

  datadogRum.startSessionReplayRecording();
}

export default function App() {
  const { hash } = useLocation();
  const { user } = useUser();
  const { userId = "" } = useParams();

  useV2ColorDesigns();

  bugsnagClient.setUser(user.authedUserId, user.email);

  datadogRum.setUser({
    id: user.authedUserId,
    name: user.email,
    email: user.email,
    companyName: user.companyName ?? "",
    displayUserId: user.displayUserId,
    displayUserName: `${user.firstName} ${user.lastName}`,
    isAdmin: user.isAdmin,
  });

  useEffectOnce(() => {
    moment.tz.setDefault(user.timezone);
  });

  return (
    <MaintenanceBoundary userData={user}>
      <CookiesProvider>
        <ApolloProvider client={user.apolloClient}>
          <UserDetailsProvider>
            <FeatureFlagsProvider>
              <DevelopmentAnalyticsDataErrorBoundary>
                <CustomGoalsProvider>
                  <SalesBreakdownConfigsProvider>
                    <CustomerBreakdownConfigProvider>
                      <UniversalBenchmarksProvider>
                        <AppVersionProvider>
                          <DndProvider backend={HTML5Backend}>
                            <UniversalBenchmarksModalProvider>
                              <UserAttributionSettingProvider>
                                <CommandPalette>
                                  <PreActivationContent>
                                    <LogOnMount
                                      name="App Load"
                                      data={{
                                        Impersonating:
                                          user.impersonatedUser?.id || "",
                                      }}
                                    />
                                    <Chrome>
                                      <NotificationManager />
                                      <Routes>
                                        {user.featureFlags
                                          .enableDashboardV2 && (
                                          <Route
                                            path="/"
                                            element={
                                              <Navigate to="home/overview" />
                                            }
                                          />
                                        )}
                                        <Route
                                          path="home"
                                          element={<Navigate to="overview" />}
                                        />
                                        <Route
                                          path="attribution"
                                          element={<Navigate to="sales" />}
                                        />
                                        <Route
                                          path="labs"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <LabsPage />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="home/overview"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <OverviewV2Page />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="attribution"
                                          element={<AttributionLayout />}
                                        >
                                          {user.featureFlags
                                            .enableCreativeAnalytics && (
                                            <Route
                                              path="creative-analytics"
                                              element={
                                                <Suspense fallback={<></>}>
                                                  <CreativeAnalyticsPage />
                                                </Suspense>
                                              }
                                            />
                                          )}
                                          {user.featureFlags
                                            .productAnalytics && (
                                            <Route
                                              path="product-analytics"
                                              element={
                                                <Suspense fallback={<></>}>
                                                  <ProductAnalytics />
                                                </Suspense>
                                              }
                                            />
                                          )}
                                          <Route
                                            path="orders"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <OrdersV2 />
                                              </Suspense>
                                            }
                                          />
                                          <Route
                                            path="orders/:orderId/*"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <OrderPage />
                                              </Suspense>
                                            }
                                          />
                                          <Route
                                            path="sales"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <SalesV4Page />
                                              </Suspense>
                                            }
                                          />
                                        </Route>
                                        <Route
                                          path="/"
                                          element={<Navigate to="overview" />}
                                        />
                                        {user.featureFlags
                                          .enableCreativeAnalytics && (
                                          <Route
                                            path="creative-analytics"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <CreativeAnalyticsPage />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        {user.featureFlags.productAnalytics && (
                                          <Route
                                            path="product-analytics"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <ProductAnalytics />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        <Route
                                          path="orders"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <OrdersPage />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="orders/:orderId/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <OrderPage />
                                            </Suspense>
                                          }
                                        />
                                        {user.featureFlags.overviewPageV2 &&
                                        !user.featureFlags
                                          .doNotShowNewOverviewPage ? (
                                          <Route
                                            path="overview"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <OverviewV2Page />
                                              </Suspense>
                                            }
                                          />
                                        ) : (
                                          <Route
                                            path="overview"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <OverviewPage />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        {user.featureFlags
                                          .doNotShowNewSalesPage ? (
                                          <Route
                                            path="sales"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <FastSalesPage />
                                              </Suspense>
                                            }
                                          />
                                        ) : (
                                          <Route
                                            path="sales"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <SalesV3Page />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        <Route
                                          path="settings/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <SettingsRoutes />
                                            </Suspense>
                                          }
                                        />
                                        <>
                                          <Route
                                            path="breakdowns/*"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <BreakdownsRoutes />
                                              </Suspense>
                                            }
                                          />
                                          <Route
                                            path="label-assignment"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <LabelAssignmentPage />
                                              </Suspense>
                                            }
                                          />
                                        </>
                                        {user.featureFlags.enableMMM && (
                                          <Route
                                            path="mmm"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <MMMPage />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        <Route
                                          path="guides/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <GuideRoutes />
                                            </Suspense>
                                          }
                                        />
                                        {user.displayUserId ===
                                          "dd62a9c5-ca2d-4231-a4bf-0cb5acc56c49" &&
                                          user.featureFlags
                                            .showFarmstandPurchases && (
                                            <Route
                                              path="farmstand-purchases"
                                              element={
                                                <Suspense fallback={<></>}>
                                                  <FarmstandPurchasesTablePage />
                                                </Suspense>
                                              }
                                            />
                                          )}
                                        <Route
                                          path="rules/*"
                                          element={
                                            <Navigate
                                              to={{
                                                pathname: `/${userId}/breakdowns`,
                                              }}
                                            />
                                          }
                                        />
                                        <Route
                                          path="overview-print"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <OverviewPrintPage />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="sales/objects/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <ObjectEditorRoutes />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="objects/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <ObjectEditorRoutes />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="breakdown-editor"
                                          element={
                                            <Navigate
                                              to={{
                                                pathname: `/${userId}/breakdowns`,
                                              }}
                                            />
                                          }
                                        />
                                        <Route
                                          path="model-comparison"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <ModelComparisonPage />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="model-comparison/objects/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <ObjectEditorRoutes />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="customers/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <CustomersRoutes />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="monitor/*"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <MonitorRoutes />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="redirects/*"
                                          element={<Redirects />}
                                        />
                                        <Route
                                          path="onboarding"
                                          element={
                                            <Navigate
                                              to={{
                                                pathname: `/${userId}/settings/${hash.replace(
                                                  "#",
                                                  "",
                                                )}`,
                                              }}
                                            />
                                          }
                                        />
                                        {user.featureFlags
                                          .enableMetricsExplorer && (
                                          <Route
                                            path="metrics-explorer"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <MetricsExplorerPage />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        {user.featureFlags
                                          .enableLandingPageAnalyticsDashboard && (
                                          <Route
                                            path="labs/landing-page-analytics"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <LandingPageAnalyticsPage />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        {user.featureFlags
                                          .enableNotificationsLab && (
                                          <Route
                                            path="labs/notifications"
                                            element={
                                              <Suspense fallback={<></>}>
                                                <NotificationsPage />
                                              </Suspense>
                                            }
                                          />
                                        )}
                                        {/* demo endpoint */}
                                        <Route
                                          path="progress"
                                          element={
                                            <Suspense fallback={<></>}>
                                              <ProgressPage />
                                            </Suspense>
                                          }
                                        />
                                        <Route
                                          path="/*"
                                          element={<Page404 />}
                                        />
                                      </Routes>
                                    </Chrome>
                                  </PreActivationContent>
                                </CommandPalette>
                              </UserAttributionSettingProvider>
                            </UniversalBenchmarksModalProvider>
                          </DndProvider>
                        </AppVersionProvider>
                      </UniversalBenchmarksProvider>
                    </CustomerBreakdownConfigProvider>
                  </SalesBreakdownConfigsProvider>
                </CustomGoalsProvider>
              </DevelopmentAnalyticsDataErrorBoundary>
            </FeatureFlagsProvider>
          </UserDetailsProvider>
        </ApolloProvider>
      </CookiesProvider>
    </MaintenanceBoundary>
  );
}
